import { useEffect, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';

import { useGradeFlow } from '@/states/grade-flow';
import { useNavigateFlow } from '@/hooks/grade-flow/use-navigate-flow';
import { toast_error } from '@/libs/toast-wrappers';
import { FormTypes } from '@/states/grade-flow/types';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { FooterButtons, LayoutStep } from './layouts-flow';
import { GoBackStep } from './go-back-step-btn';
// forms of different diseases
import { DiabeticQuestions } from './diabetic-retinopathy/diabetic-questions';
import { HypertensiveForm } from './hypertensive-retinopathy/hypertensive-questions';

export function QuestionsStep() {
  const { t } = useTranslation();
  const { navigateFlow } = useNavigateFlow();
  const [open, setOpen] = useState<FormTypes>();

  const { hasLaterality, left, right, both, isReadable, forms } = useGradeFlow(
    useShallow((s) => ({
      isReadable: s.form.is_readable,
      hasLaterality: s.isLateralityPresentOnImgs,
      left: s.form.grade_left_eye,
      right: s.form.grade_right_eye,
      both: s.form.grade_both_eyes,
      forms: s.forms,
    }))
  );

  const multiforms = useMemo(() => {
    return forms.length > 1;
  }, [forms]);

  const isCurrentLastForm = multiforms && open ? forms.indexOf(open) === forms.length - 1 : false;
  const isCurrentFirstForm = multiforms && open ? forms.indexOf(open) === 0 : false;

  const isBlockedByEvidence =
    (hasLaterality &&
      (!left?.evidence_of_dme === undefined || !right?.evidence_of_dme === undefined)) ||
    (!hasLaterality && !both?.evidence_of_dme === undefined);

  const isBlockedByGrade =
    (hasLaterality && (!left?.dr_grade_scale || !right?.dr_grade_scale)) ||
    (!hasLaterality && !both?.dr_grade_scale);

  const handleContinue = () => {
    if (forms.includes('diabetic') && open === 'diabetic') {
      if (isBlockedByEvidence) {
        toast_error(t('grade-error-evidence-dme'));
        return;
      }
      if (isBlockedByGrade) {
        toast_error(t('grade-error-dr-grade'));
        return;
      }
    }

    if (multiforms && open) {
      // open the next form
      const currentIndex = forms.indexOf(open);
      // move to the next form if needed
      if (currentIndex < forms.length - 1) {
        setOpen(forms[currentIndex + 1]);
        return;
      }
    }

    navigateFlow('GRADING_CASE_STEP_4');
  };

  const handlePrevForm = () => {
    if (multiforms && open) {
      const currentIndex = forms.indexOf(open);
      // move to the previous form if needed
      if (currentIndex > 0) {
        setOpen(forms[currentIndex - 1]);
        return;
      }
    }
  };

  useEffect(() => {
    if (!isReadable) {
      navigateFlow('GRADING_CASE_STEP_2');
    }
    if (multiforms) {
      setOpen(forms[0]);
    } else if (forms.includes('diabetic')) {
      setOpen('diabetic');
    } else if (forms.includes('hypertensive')) {
      setOpen('hypertensive');
    }
  }, [isReadable, navigateFlow]);

  return (
    <LayoutStep>
      {multiforms ? (
        <>
          <Collapsible asChild open={open === 'diabetic'}>
            <CollapsibleContent>
              <DiabeticQuestions />
            </CollapsibleContent>
          </Collapsible>
          <Collapsible asChild open={open === 'hypertensive'}>
            <CollapsibleContent>
              <HypertensiveForm />
            </CollapsibleContent>
          </Collapsible>
        </>
      ) : (
        <>
          {forms.includes('diabetic') && <DiabeticQuestions />}
          {forms.includes('hypertensive') && <HypertensiveForm />}
        </>
      )}
      <FooterButtons>
        {isCurrentFirstForm || !multiforms ? (
          <GoBackStep backTo="GRADING_CASE_STEP_2" />
        ) : (
          <Button variant="ghost-muted" onClick={handlePrevForm}>
            {t('previous-form')}
          </Button>
        )}
        <Button onClick={handleContinue}>
          {multiforms && !isCurrentLastForm ? t('next-form') : t('continue')}
        </Button>
      </FooterButtons>
    </LayoutStep>
  );
}
